import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { LMSecurityReport } from '../lm-reports';
import { SohoDataGridComponent } from 'ids-enterprise-ng';
import { LMGlobalUtilService } from '@app/utils';
import { LMApplicationMenuSubMenuService } from '@app/components';
import { LMUrlUtilService } from '@app/services';

@Component({
  selector:    'lm-reports-security-list',
  templateUrl: 'lm-reports-security-list.component.html',
  styleUrls:   ['lm-reports-security-list.component.scss'],
  })

export class LMReportsSecurityListComponent implements AfterViewInit, OnInit, OnDestroy {

  DataGridFormatter = (row: number, cell: number, value: any, dataGridColumn: SohoDataGridColumn, data: LMSecurityReport, dataGrid: SohoDataGridStatic): string =>
      this.targetFormatter(row, cell, value, dataGridColumn, data, dataGrid);

  private titleColumn: ListGridColumn = {
    id:         'title',
    name:        Soho.Locale.translate('Title'),
    field:      'title',
    cssClass:   'text-strong',
    formatter:  Soho.Formatters.Tree,
    singleline: true,
    sortable:   false,
    filterType:     'text',
    filterConditions: ['contains'],
  };
  private notAccessibleColumn: ListGridColumn = {
    id:         'notAccessible',
    name:       Soho.Locale.translate('NotAccessible'),
    field:      'notAccessible',
    align:      'center',
    filterType:     'select',
    options:    [ { label: Soho.Locale.translate('Yes'), value: true }, { label: Soho.Locale.translate('No'), value: false } ],
    formatter:  Soho.Formatters.Checkbox,
    editor:     Soho.Editors.Checkbox,
    isEditable:      (row: number, cell: any, fieldValue: any, columnDef: SohoDataGridColumn, rowData: any): boolean =>  this.isEditable(row, cell, fieldValue, columnDef, rowData),
    singleline: true,
    sortable:   false,
  };

  private hasSuggestionColumn: ListGridColumn = {
  id:             'hasSuggestion',
  name:           Soho.Locale.translate('HasSuggestion'),
  field:          'hasSuggestion',
  sortable:       false,
  filterType:     'select',
  options:        [ { label: Soho.Locale.translate('Yes'), value: true }, { label: Soho.Locale.translate('No'), value: false } ],
  singleline:     true,
  align:          'center',
  formatter:      this.DataGridFormatter,
  };

  private typeColumn: ListGridColumn = {
    id:         'type',
    name:       Soho.Locale.translate('Type'),
    field:      'type',
    formatter:  this.reportTypeFormatter,
    singleline: true,
    sortable:   false,
    filterType:     'text',
    filterConditions: ['contains'],
  };
  private nameColumn: ListGridColumn = {
    id:         'type',
    name:       Soho.Locale.translate('Name'),
    field:      'name',
    formatter:  this.reportNameFormatter,
    singleline: true,
    sortable:   false,
    filterType:     'text',
    filterConditions: ['contains'],
    click:      (e: Event, args: SohoDataGridColumnClickData[]) => this.onReportNameClick(e, args),
  };
  private businessClassColumn: ListGridColumn = {
    id:         'busClass',
    name:       Soho.Locale.translate('BusinessClass'),
    field:      'busClass',
    formatter:  Soho.Formatters.Text,
    singleline: true,
    sortable:   false,
    filterType:     'text',
    filterConditions: ['contains'],
  };


  columnGridOptions: SohoDataGridOptions;
  flattenData: Array<LMSecurityReport>;

  private securityListColumns: SohoDataGridColumn[] = [
    this.titleColumn,
    this.notAccessibleColumn,
    this.hasSuggestionColumn,
    this.typeColumn,
    this.nameColumn,
    this.businessClassColumn
  ];


  @Input() reportsArray: Array<LMSecurityReport>;
  @Input() uniqueId: string;

  @Output() notAccessibleClicked = new EventEmitter<any>();

  @ViewChild(SohoDataGridComponent) sohoDataGridComponent: SohoDataGridComponent;
  @ViewChild('reportFormDialog', { read: ViewContainerRef, static: true }) reportPlaceholder: ViewContainerRef;

  constructor(
    public lmGlobalUtilService: LMGlobalUtilService,
    public lmUrlUtilService: LMUrlUtilService,
    protected lmApplicationMenuSubMenuService: LMApplicationMenuSubMenuService
  ) {}

  ngOnInit(){
    this.columnGridOptions = <SohoDataGridOptions>{
      columns:                  this.securityListColumns,
      dataset:                  this.reportsArray,
      uniqueId:                 this.uniqueId,
      selectable:               'mixed',
      frozenColumns:            { left: ['title'] },
      editable:                 true,
      filterable:               false, // soho has issue editing a filtered grid.
      paging:                   false,
      treeGrid:                 true,
      showDirty:                false,
      rowHeight:                'short',
      clickToSelect:            true,
      headerBackgroundColor:    'light',
    };

    this.flatten(this.reportsArray);
  }

  ngOnDestroy(): void {
  }

  ngAfterViewInit(): void {
  }

  onPropertiesChange(event: SohoDataGridCellChangeEvent) {
    if (!event) {
      return;
    }

    const row = event.row;
    const secure = event.value;
    const data = this.flattenData[row];

    this.updateChildrenState(data, secure);

    if (this.sohoDataGridComponent) {
      this.sohoDataGridComponent.updated();
    }

    this.notAccessibleClicked.emit({ secure, data });
  }

  private isEditable(row: number, cell: any, fieldValue: any, columnDef: SohoDataGridColumn, rowData: LMSecurityReport): boolean {
    return !rowData.disabled;
  }

  private onReportNameClick = ( e: Event, args: SohoDataGridColumnClickData[]) => {
    if (args?.length > 0) {
      const report: LMSecurityReport = args[0].item;

      if (report.url) {
        if (report.type === 'task') {
          this.lmApplicationMenuSubMenuService.requestActionFromPageMenu(report.url, report.id, this.reportPlaceholder);
        } else {
          this.lmGlobalUtilService.navigate(report.url, undefined, 'external');
        }
      }
    }
  }

  private reportTypeFormatter(row, cell, value, col, item: LMSecurityReport, api) {
    let display = value;

    switch (item.type) {
      case 'task':
        display = Soho.Locale.translate('task');
        break;
      case 'page':
        display = Soho.Locale.translate('page');
        break;
      case 'list':
        display = Soho.Locale.translate('list');
        break;
      case 'module':
        display = Soho.Locale.translate('ReportCatalogCategory');
        break;
      case 'category':
        display = Soho.Locale.translate('ReportCatalogSubcategory');
        break;
    }

    return  Soho.Formatters.Text(row, cell, display, col, item, api);
  }

  private reportNameFormatter(row, cell, value, col, item: LMSecurityReport, api) {
    if (item.url) {
      return  Soho.Formatters.Hyperlink(row, cell, value, col, item, api);
    } else {
      return  Soho.Formatters.Ellipsis(row, cell, value, col, item, api);
    }
  }

  private updateChildrenState(item: LMSecurityReport, disabled: boolean) {
    if (item.children) {
      for (const child of item.children) {
        child.disabled = disabled;
        child.notAccessible = disabled;
        this.updateChildrenState(child, disabled);
      }
    }
  }

  private _updateInaccessible(itemKey: string, items: Array<LMSecurityReport>){
    if (items) {
      items.forEach(item => {
        if (item.id === itemKey) {
          item.notAccessible = false;
          this.updateChildrenState(item, false);
          return;
        }
        this._updateInaccessible(itemKey, item.children);
      });
    };
  }

  private flatten(items: Array<LMSecurityReport>) {
    const list = [];
    this._flatten(items, list);
    this.flattenData = list;
  }

  private _flatten(children: Array<LMSecurityReport>, flatList: Array<LMSecurityReport>) {
    if (children) {
      children.forEach((item) => {
        flatList.push(item);
        this._flatten(item.children, flatList);
      });
    }
  }

  public updateInaccessible(itemKey: string) {
    this._updateInaccessible(itemKey, this.reportsArray);

    if (this.sohoDataGridComponent) {
      this.sohoDataGridComponent.updated();
    }
  }

  private targetFormatter = (
      row: number,
      cell: number,
      value: any,
      dataGridColumn: SohoDataGridColumn,
      data: LMSecurityReport,
      dataGrid: SohoDataGridStatic
  ): string => {
      if (dataGridColumn.id === 'hasSuggestion') {
        if (data.hasSuggestion) {
          return data.suggestion === 'inaccessible' ? 'Hide' : 'Show';
        }
        return '';

      } else {
      const translation = Soho.Locale.translate(value);
      return  Soho.Formatters.Ellipsis(row, cell, translation, dataGridColumn, data, dataGrid);
    }
  }
}
